// Brand kit helpers (WS-2.5 dashboard surface). The generate endpoint takes
// raw base64 (no data: prefix) plus palette hexes; these helpers do the
// client-side prep. Pixel math is pure and unit-tested; DOM/File plumbing
// stays thin.

export interface BrandKitPreset {
  key: string;
  label: string;
  bgColorHex: string;
  textColorHex: string;
}

export class BrandKitUtils {
  static readonly MAX_LOGO_BYTES = 8 * 1024 * 1024;

  static readonly PRESETS: BrandKitPreset[] = [
    { key: 'dark', label: 'Dark', bgColorHex: '#101820', textColorHex: '#ffffff' },
    { key: 'light', label: 'Light', bgColorHex: '#f5f5f2', textColorHex: '#1a1a1a' },
  ];

  /** Strips any data-URL prefix — the API wants raw base64. */
  static toRawBase64(dataUrl: string): string {
    const comma = dataUrl.indexOf(',');
    return comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl;
  }

  /**
   * Average color of the opaque pixels in RGBA data (the "from logo" accent
   * suggestion). Returns null when nothing is opaque enough to average.
   */
  static averageColorHex(rgba: Uint8ClampedArray): string | null {
    let r = 0;
    let g = 0;
    let b = 0;
    let n = 0;
    for (let i = 0; i + 3 < rgba.length; i += 4) {
      if (rgba[i + 3] < 128) continue; // transparent pixels say nothing about the brand
      r += rgba[i];
      g += rgba[i + 1];
      b += rgba[i + 2];
      n++;
    }
    if (n === 0) return null;
    return BrandKitUtils.rgbToHex(Math.round(r / n), Math.round(g / n), Math.round(b / n));
  }

  static rgbToHex(r: number, g: number, b: number): string {
    const c = (v: number) => Math.max(0, Math.min(255, v)).toString(16).padStart(2, '0');
    return `#${c(r)}${c(g)}${c(b)}`;
  }

  /** Reads a File as raw base64 (data-URL prefix stripped). */
  static async fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(BrandKitUtils.toRawBase64(String(reader.result)));
      reader.onerror = () => reject(new Error('Could not read the file.'));
      reader.readAsDataURL(file);
    });
  }

  /** Reads a File's pixel dimensions; null when undecodable. */
  static async readImageDimensions(file: File): Promise<{ w: number; h: number } | null> {
    const url = URL.createObjectURL(file);
    try {
      return await new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight });
        img.onerror = () => resolve(null);
        img.src = url;
      });
    } finally {
      URL.revokeObjectURL(url);
    }
  }

  /**
   * PURE slot-pick verdict (vitest-covered): filename/size/dims against a
   * slot spec. dims null = undecodable. Returns an error message or null.
   */
  static slotFileProblem(
    filename: string,
    sizeBytes: number,
    dims: { w: number; h: number } | null,
    slot: { label: string; w: number; h: number; format: 'png' | 'jpg' },
    maxBytes: number,
  ): string | null {
    const ext = filename.toLowerCase().split('.').pop() ?? '';
    const wantExts = slot.format === 'png' ? ['png'] : ['jpg', 'jpeg'];
    if (!wantExts.includes(ext)) {
      return `${slot.label} must be a ${slot.format.toUpperCase()} file.`;
    }
    if (sizeBytes > maxBytes) {
      return `${slot.label} must be under ${Math.round(maxBytes / 1024 / 1024)}MB.`;
    }
    if (!dims) {
      return `${slot.label} could not be decoded — use a valid ${slot.format.toUpperCase()}.`;
    }
    if (dims.w !== slot.w || dims.h !== slot.h) {
      return `${slot.label} must be exactly ${slot.w} x ${slot.h} pixels (this image is ${dims.w} x ${dims.h}).`;
    }
    return null;
  }

  /**
   * Browser wrapper: validates one manual-set slot pick (fast feedback;
   * the factory re-validates authoritatively).
   */
  static async validateSlotFile(
    file: File,
    slot: { label: string; w: number; h: number; format: 'png' | 'jpg' },
    maxBytes: number,
  ): Promise<string | null> {
    const ext = file.name.toLowerCase().split('.').pop() ?? '';
    const wantExts = slot.format === 'png' ? ['png'] : ['jpg', 'jpeg'];
    // Cheap checks first — skip the decode entirely on wrong type/size.
    if (!wantExts.includes(ext) || file.size > maxBytes) {
      return BrandKitUtils.slotFileProblem(file.name, file.size, null, slot, maxBytes);
    }
    const dims = await BrandKitUtils.readImageDimensions(file);
    return BrandKitUtils.slotFileProblem(file.name, file.size, dims, slot, maxBytes);
  }
}
